import React, { useState } from "react";
import { Modal, Button, Badge, Spinner } from "react-bootstrap";
import { FiDownload, FiX } from "react-icons/fi";
import moment from "moment-timezone";
import html2pdf from "html2pdf.js";
import { toast } from "react-toastify";

const ApplicationViewModal = ({ show, onHide, application, type = "applicant" }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const downloadAsPDF = async () => {
        setIsDownloading(true);

        try {
            const element = document.getElementById("application-pdf-content");

            const options = {
                margin: [5, 0, 0, 0],
                filename: `${type}-loan-application-${application?.id_number || "form"}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    allowTaint: false,
                    backgroundColor: "#ffffff",
                    logging: false,
                    scrollY: -window.scrollY
                },
                jsPDF: {
                    unit: "mm",
                    format: "a4",
                    orientation: "portrait"
                },
                pagebreak: {
                    mode: ["avoid-all", "css", "legacy"],
                    before: '.page-break-before',
                    after: '.page-break-after',
                    avoid: ['.no-break', '.section-container', '.field-group']
                }
            };

            await html2pdf().set(options).from(element).save();

            toast.success("PDF downloaded successfully!");
            onHide();
        } catch (error) {
            console.error("PDF download failed:", error);
            toast.error("Failed to download PDF. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        return moment(dateString).format("DD/MM/YYYY");
    };

    const getShopName = () => {
        return "AZZ ENTERPRISES PTE. LTD";
    };

    const DocumentStatus = ({ hasDoc }) => (
        <span style={{
            color: hasDoc ? '#059669' : '#dc2626',
            fontWeight: 'bold',
            fontSize: '12px',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: hasDoc ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${hasDoc ? '#bbf7d0' : '#fecaca'}`
        }}>
            {hasDoc ? '✓ Provided' : '✗ Not Provided'}
        </span>
    );

    const SectionHeader = ({ title, number }) => (
        <div style={{
            backgroundColor: '#1e40af',
            color: 'white',
            padding: '12px 20px',
            borderBottom: '2px solid #1e40af',
            marginBottom: '0'
        }}>
            <h5 style={{
                margin: '0',
                fontWeight: 'bold',
                fontSize: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <span style={{
                    backgroundColor: 'white',
                    color: '#1e40af',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold'
                }}>
                    {number}
                </span>
                {title}
            </h5>
        </div>
    );

    const FieldGroup = ({ label, value, isDocument = false, hasDoc = false }) => (
        <div className="field-group no-break" style={{
            display: 'flex',
            padding: '10px 15px',
            borderBottom: '1px solid #f1f5f9',
            alignItems: 'flex-start',
            minHeight: '42px'
        }}>
            <div style={{
                width: '40%',
                fontWeight: '600',
                color: '#374151',
                fontSize: '12px',
                paddingRight: '15px',
                lineHeight: '1.4'
            }}>
                {label}:
            </div>
            <div style={{
                width: '60%',
                fontWeight: '500',
                color: isDocument ? 'inherit' : '#1e40af',
                fontSize: '12px',
                wordWrap: 'break-word',
                lineHeight: '1.4'
            }}>
                {isDocument ? <DocumentStatus hasDoc={hasDoc} /> : value}
            </div>
        </div>
    );

    const applicationSections = [
        ...(type === "applicant" ? [{
            title: "Loan Details",
            number: "1",
            fields: [
                { label: "Type of Loan", value: application?.loan_type || "EMI Against Property" },
                { label: "Loan Amount Requested", value: application?.loan_amt ? `$${parseInt(application.loan_amt).toLocaleString()}` : "—" },
                { label: "Required Tenor", value: application?.tenor ? `${application.tenor} months` : "—" },
                { label: "Purpose of Loan", value: application?.loan_purpose || "Property Lending" },
                { label: "Property Model", value: application?.model || "—" },
                { label: "IMEI Number", value: application?.imei_no || "—" }
            ]
        }] : []),
        {
            title: type === "applicant" ? "Applicant Details" : "Referrer Details",
            number: type === "applicant" ? "2" : "1",
            fields: [
                { label: `Full Name`, value: application?.name || "—" },
                { label: "Photograph", isDocument: true, hasDoc: !!application?.photo_s3 },
                { label: "Father's/Spouse's Name", value: application?.fs_name || "—" },
                { label: "Nationality", value: application?.nation || "—" },
                { label: "Marital Status", value: application?.marital || "—" },
                { label: "Date of Birth", value: formatDate(application?.dob) },
                { label: "Gender", value: application?.gender || "—" },
                { label: "Work Permit Type", value: application?.permit || "—" },
                { label: "Residential Address", value: application?.address || "—" },
                { label: "Landmark", value: application?.landmark || "—" },
                { label: "Mobile Number", value: application?.mob_num || "—" },
                { label: "Email Address", value: application?.email_id || "—" }
            ]
        },
        {
            title: "Mandatory Documents",
            number: type === "applicant" ? "3" : "2",
            fields: [
                { label: "Passport Copy", isDocument: true, hasDoc: !!application?.passport_s3 },
                { label: "IC Copy", isDocument: true, hasDoc: !!application?.ic_s3 }
            ]
        },
        {
            title: "Additional Documents",
            number: type === "applicant" ? "4" : "3",
            fields: [
                { label: "Aadhaar Card", isDocument: true, hasDoc: !!application?.aadhaar_s3 },
                { label: "PAN Card", isDocument: true, hasDoc: !!application?.pan_s3 },
                { label: "Address Proof", value: application?.address_s3 },
                { label: "Native Contact Number", value: application?.contact_no || "—" }
            ]
        },
        {
            title: "Employment Details",
            number: type === "applicant" ? "5" : "4",
            fields: [
                { label: "Employment Type", value: application?.emp_type || "—" },
                { label: "Professional ID", value: application?.prof_id || "—" },
                { label: "Designation", value: application?.designation || "—" },
                { label: "Business Constitution", value: application?.bus_const || "—" },
                { label: "Total Experience", value: application?.total_exp ? `${parseFloat(application.total_exp)} years` : "—" },
                { label: "Office Address", value: application?.off_address || "—" },
                { label: "Office Landmark", value: application?.off_landmark || "—" },
                { label: "City", value: application?.off_city || "—" }
            ]
        }
    ];

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton className="border-bottom-0 bg-light">
                <Modal.Title className="d-flex align-items-center gap-2">
                    <Badge bg={type === "applicant" ? "primary" : "success"} className="fs-6">
                        {type === "applicant" ? "Applicant" : "Referrer"} Application View
                    </Badge>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0">
                <div id="application-pdf-content" style={{
                    padding: '25px',
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    fontSize: '13px',
                    lineHeight: '1.5',
                    backgroundColor: '#ffffff',
                    color: '#374151'
                }}>
                    {/* Professional Header */}
                    <div className="no-break" style={{
                        borderBottom: '4px solid #1e40af',
                        paddingBottom: '20px',
                        marginBottom: '25px',
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                        padding: '25px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '15px',
                            marginBottom: '15px'
                        }}>
                            <div>
                                <h2 style={{
                                    margin: '0 0 5px 0',
                                    fontWeight: 'bold',
                                    fontSize: '22px',
                                    color: '#1e40af',
                                    letterSpacing: '0.5px'
                                }}>
                                    {getShopName()}
                                </h2>
                                <h3 style={{
                                    margin: '0',
                                    fontWeight: '600',
                                    fontSize: '16px',
                                    color: '#475569'
                                }}>
                                    EMI Loan Application Form/Loan Agreement
                                </h3>
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '30px',
                            fontSize: '12px',
                            color: '#64748b',
                            marginTop: '15px',
                            flexWrap: 'wrap'
                        }}>
                            <div>
                                <strong>Application ID:</strong> {application?.id_number || "N/A"}
                            </div>
                            <div>
                                <strong>Created Date:</strong> {moment()
                                    .tz("Asia/Singapore")
                                    .format("DD/MM/YYYY")
                                }
                            </div>
                            <div>
                                <strong>Type:</strong> {type === "applicant" ? "Applicant" : "Referrer"}
                            </div>
                        </div>
                    </div>

                    {type === "applicant" && (
                        <div className="no-break" style={{
                            marginBottom: '25px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                        }}>
                            <SectionHeader title="Document Checklist & Requirements" number="📋" />
                            <div style={{ padding: '20px' }}>
                                <div style={{ marginBottom: '20px' }}>

                                    <div style={{ marginBottom: '15px' }}>
                                        <div style={{
                                            backgroundColor: '#fef2f2',
                                            border: '1px solid #fecaca',
                                            borderRadius: '6px',
                                            padding: '12px',
                                            marginBottom: '12px'
                                        }}>
                                            <strong style={{ color: '#dc2626', fontSize: '12px' }}>
                                                Mandatory Documents Required:
                                            </strong>
                                            <ul style={{
                                                margin: '8px 0 0 0',
                                                paddingLeft: '20px',
                                                fontSize: '11px',
                                                color: '#374151'
                                            }}>
                                                <li>Colour Photograph (Passport Size)</li>
                                                <li>Valid Passport Copy</li>
                                                <li>Proof of Residence (Utility Bill/Rental Agreement)</li>
                                                <li>Income Verification - Salary Slips (Last 4 Months)</li>
                                                <li>Bank Statements (Last 4 Months)</li>
                                                <li>IC Copy (Front & Back)</li>
                                            </ul>
                                        </div>

                                        <div style={{
                                            backgroundColor: '#fffbeb',
                                            border: '1px solid #fed7aa',
                                            borderRadius: '6px',
                                            padding: '12px'
                                        }}>
                                            <strong style={{ color: '#d97706', fontSize: '12px' }}>
                                                Supporting Documents (Optional):
                                            </strong>
                                            <ul style={{
                                                margin: '8px 0 0 0',
                                                paddingLeft: '20px',
                                                fontSize: '11px',
                                                color: '#374151'
                                            }}>
                                                <li>Aadhaar Card Copy</li>
                                                <li>PAN Card Copy</li>
                                                <li>NRIC/FIN Copy</li>
                                                <li>Employment Certificate</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Application Details */}
                    <div className="no-break text-center" style={{
                        marginBottom: '25px',
                        padding: '15px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <h3 style={{
                            margin: '0 0 8px 0',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            color: '#1e40af'
                        }}>
                            Application Details
                        </h3>
                        <p style={{
                            margin: '0',
                            fontWeight: '500',
                            fontSize: '13px',
                            color: '#64748b'
                        }}>
                            Complete {type === "applicant" ? "Applicant" : "Referrer"} Information & Verification
                        </p>
                    </div>

                    {/* Application Sections */}
                    {applicationSections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="no-break section-container" style={{
                            marginBottom: '20px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}>
                            <SectionHeader title={section.title} number={section.number} />
                            <div style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #f1f5f9'
                            }}>
                                {section.fields.map((field, fieldIndex) => (
                                    <FieldGroup
                                        key={fieldIndex}
                                        label={field.label}
                                        value={field.value}
                                        isDocument={field.isDocument}
                                        hasDoc={field.hasDoc}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Enhanced Declaration Section */}
                    <div className="no-break" style={{
                        marginTop: '30px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{
                            backgroundColor: '#1e40af',
                            color: 'white',
                            padding: '15px 25px',
                            textAlign: 'center'
                        }}>
                            <h4 style={{
                                margin: '0',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px'
                            }}>
                                Declaration & Undertaking
                            </h4>
                        </div>

                        <div style={{ padding: '25px' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <p style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '12px',
                                    textAlign: 'justify',
                                    lineHeight: '1.6',
                                    color: '#374151'
                                }}>
                                    I, the undersigned, hereby declare that all information provided in this application form and the accompanying documents is true, accurate, and complete to the best of my knowledge and belief. I understand that any false, misleading, or incomplete information may result in the rejection of my loan application or may lead to legal consequences.
                                </p>

                                <p style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '12px',
                                    textAlign: 'justify',
                                    lineHeight: '1.6',
                                    color: '#374151'
                                }}>
                                    I hereby authorise AZZ Enterprises and its authorised representatives to verify any information provided in this form, including but not limited to contacting my employer, bank, or any other relevant institution. I consent to the use of my personal information for the purpose of processing this loan application and related administrative actions, in compliance with applicable data protection laws.
                                </p>

                                <p style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '12px',
                                    textAlign: 'justify',
                                    lineHeight: '1.6',
                                    color: '#374151'
                                }}>
                                    I further authorise AZZ Enterprises to share my information with the Ministry of Manpower (MOM) and the Immigration & Checkpoints Authority (ICA), acknowledging that such disclosure may affect my employment pass or renewal process.
                                </p>

                                <p style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '12px',
                                    textAlign: 'justify',
                                    lineHeight: '1.6',
                                    color: '#374151'
                                }}>
                                    I also authorise AZZ Enterprises to block my mobile device using its IMEI number, if deemed necessary, as part of the loan enforcement or recovery process.
                                </p>

                                <p style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '12px',
                                    textAlign: 'justify',
                                    lineHeight: '1.6',
                                    color: '#374151'
                                }}>
                                    I undertake to safeguard the handset provided under this agreement and ensure it remains in good and workable condition until full repayment of the loan has been made to AZZ Enterprises.
                                </p>

                                <p style={{
                                    margin: '0 0 20px 0',
                                    fontSize: '12px',
                                    textAlign: 'justify',
                                    lineHeight: '1.6',
                                    color: '#374151'
                                }}>
                                    I hereby declare that I have read, understood, and agreed to the terms and conditions of the loan and undertake to comply with all obligations arising from the loan agreement if sanctioned.
                                </p>
                            </div>

                            {/* Signature Section */}
                            <div style={{
                                borderTop: '2px dashed #e2e8f0',
                                paddingTop: '20px',
                                marginTop: '20px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                                    <div style={{ width: '100%' }}>
                                        <div style={{
                                            marginBottom: '15px'
                                        }}>
                                            <strong style={{
                                                fontSize: '12px',
                                                display: 'block',
                                                marginBottom: '8px',
                                                color: '#374151'
                                            }}>
                                                {type === "applicant" ? "Applicant" : "Referrer"}'s Signature:
                                            </strong>
                                            <div style={{
                                                height: '60px',
                                                border: '1px dashed #cbd5e1',
                                                padding: '15px',
                                                backgroundColor: '#f8fafc',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                            }}>
                                                <span style={{
                                                    fontSize: '11px',
                                                    color: '#94a3b8',
                                                    fontStyle: 'italic',
                                                    fontWeight: 'normal'
                                                }}>
                                                    (Sign here in presence of witness)
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <strong style={{
                                                fontSize: '12px',
                                                color: '#374151'
                                            }}>
                                                Date: {application?.created_at ? moment(application.created_at).format("DD/MM/YYYY") : "______"}
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Company Stamp Area */}
                                <div style={{
                                    marginTop: '25px',
                                    padding: '15px',
                                    border: '1px dashed #cbd5e1',
                                    borderRadius: '4px',
                                    backgroundColor: '#f8fafc',
                                    textAlign: 'center'
                                }}>
                                    <span style={{
                                        fontSize: '11px',
                                        color: '#94a3b8',
                                        fontStyle: 'italic'
                                    }}>
                                        For Office Use Only - Authorized Signatory & Company Stamp
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="no-break" style={{
                        marginTop: '20px',
                        padding: '15px',
                        textAlign: 'center',
                        fontSize: '10px',
                        color: '#64748b',
                    }}>
                        <p style={{ margin: '0 0 5px 0' }}>
                            <strong>{getShopName()}</strong> | Loan Application Form | Generated on {moment().tz('Asia/Singapore').format("DD/MM/YYYY HH:mm")} (SGT)
                        </p>
                        <p style={{ margin: '0', fontSize: '9px' }}>
                            This document requires physical signatures for validation.
                        </p>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="border-top-0 bg-light">
                <Button size="sm" variant="outline-secondary" onClick={onHide} disabled={isDownloading}>
                    <FiX className="me-1" /> Close
                </Button>
                <Button
                    size="sm"
                    variant="primary"
                    onClick={downloadAsPDF}
                    disabled={isDownloading}
                >
                    {isDownloading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Downloading...
                        </>
                    ) : (
                        <>
                            <FiDownload className="me-1" /> Download as PDF
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ApplicationViewModal;